import React, { useContext, useEffect, useRef, useState } from 'react';
import { ProductsContext } from '../../context/products/ProductContext';
import { AuthContext } from '../../context/auth';
import { isPanamaMerchant } from '../../utils/productTax';

import { Caby } from '../../models/interfaces';
import Paginator from '../../components/ui/Paginator';

const MIN_CHARS = 3;

const SearchByCode = ({ onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Caby[]>([]);
  const [pageActual, setPageActual] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { getCabysByCodeOrName, totalPage } = useContext(ProductsContext);
  const { user } = useContext(AuthContext);
  const catalog = isPanamaMerchant(user?.merchant) ? 'de la Codificación Panameña de Bienes y Servicios' : 'CABYS de Hacienda';

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef = useRef(0);

  // Solo se aplica la respuesta de la última búsqueda enviada
  const search = async (term: string, page: number) => {
    const value = term.trim();
    if (!value) return;

    const requestId = ++requestRef.current;
    setLoading(true);
    const cabys = await getCabysByCodeOrName(value, page);
    if (requestId !== requestRef.current) return;

    setResults(cabys ?? []);
    setHasSearched(true);
    setLoading(false);
  };

  // Búsqueda automática con debounce al escribir
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchTerm.trim().length < MIN_CHARS) return;

    debounceRef.current = setTimeout(() => {
      setPageActual(1);
      search(searchTerm, 1);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPageActual(1);
    search(searchTerm, 1);
  };

  const handlePageChange = (page: number) => {
    setPageActual(page);
    search(searchTerm, page);
  };

  const handleClear = () => {
    requestRef.current++;
    setSearchTerm('');
    setResults([]);
    setPageActual(1);
    setHasSearched(false);
    setLoading(false);
  };

  const renderBody = () => {
    if (loading) {
      return (
        <tr><td colSpan={4} className='text-center py-4'>
          <span className="spinner-border spinner-border-sm me-2" /> Buscando...
        </td></tr>
      );
    }
    if (!hasSearched) {
      return (
        <tr><td colSpan={4} className='text-center py-4 text-muted'>
          <i className="bi bi-search me-2" />Ingrese un código o descripción para buscar en el catálogo {catalog}.
        </td></tr>
      );
    }
    if (results.length === 0) {
      return (
        <tr><td colSpan={4} className='text-center py-4 text-muted'>
          No se encontraron resultados para "{searchTerm.trim()}".
        </td></tr>
      );
    }
    return results.map((result) => (
      <tr key={result.CodigoBienServicio} style={{ cursor: 'pointer' }} onClick={() => onSelect(result)}>
        <td className="text-muted text-nowrap" style={{ fontFamily: 'monospace' }}>{result.CodigoBienServicio}</td>
        <td>{result.DescripcionBienServicio}</td>
        <td className="text-center">
          <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">{result.Impuesto}%</span>
        </td>
        <td className="text-center">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            title="Seleccionar"
            onClick={(e) => { e.stopPropagation(); onSelect(result); }}
          >
            <i className="bi bi-check-lg me-1" />Seleccionar
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <div>
      <form className="mb-3" onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
          <input
            type="text"
            className="form-control"
            placeholder="Ingrese código o descripción (ej. café, 2391...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          {searchTerm && (
            <button className="btn btn-outline-secondary" type="button" title="Limpiar" onClick={handleClear}>
              <i className="bi bi-x-lg" />
            </button>
          )}
          <button className="btn btn-primary" type="submit" disabled={!searchTerm.trim() || loading}>
            Buscar
          </button>
        </div>
        <div className="form-text">La búsqueda inicia automáticamente al escribir {MIN_CHARS} o más caracteres.</div>
      </form>

      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr className="text-muted small text-uppercase">
              <th style={{ width: 170 }}>Código</th>
              <th>Descripción</th>
              <th className="text-center" style={{ width: 100 }}>Impuesto</th>
              <th className="text-center" style={{ width: 140 }}>Acción</th>
            </tr>
          </thead>
          <tbody>{renderBody()}</tbody>
        </table>
      </div>

      {totalPage > 1 && results.length > 0 && !loading && (
        <div className="d-flex justify-content-end">
          <Paginator totalPage={totalPage} pageActual={pageActual} onChange={handlePageChange} />
        </div>
      )}
    </div>
  );
};

export default SearchByCode;
